// Native v4 QuitButton — port of the v3 lockdown quit island
// (src/pages/experiences/components/buttons/QuitBtn.tsx +
// ChromeOSQuitButton.tsx). Per-appType branches, executeMethods bridges and
// log/LS writes verbatim (video-call precedent); renders null on web.
// Deltas (MIGRATION § DR6): antd message → sonner; v3 console.log dropped;
// empty quitButtonText falls back to CommonText.quit (v3 rendered an
// icon-only blank label).
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { AppConfig } from '@/config/appConfig';
import { useOfflineAppMethods } from '@/hooks/useOfflineAppMethods';
import { setToLS } from '@/lib/index';
import {
  checkNativeMethodSupported,
  getAppType,
  getAppVersion,
  isNewAppVersion,
  quitRequestedSuccess,
} from '@/lib/offline/OfflineAppHelper';
import { useRealtimeLogs, useRealtimeUserInfo } from '@/pages/experiences/hooks/realtime';
import { useRealtimeExperienceStore } from '@/pages/experiences/store/realtimeExperienceStore';
import { selectExperience, useExperienceStore } from '@/store/experienceStore';

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface QuitButtonProps {
  withLog?: boolean;
  userInfo?: any;
  className?: string;
  quitButtonText?: string;
  quitButtonIcon?: React.ReactNode;
  action?: string;
}

/** v3 ChromeOSQuitButton — instruction dialog, no bridge call. */
const ChromeOSQuitButton = () => {
  const { t } = useTranslation();
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="destructive" data-testid="v4-quit-cros">
          {t('CommonText.quit')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('CommonText.quit')}</DialogTitle>
          <DialogDescription>
            {t('ChromeOSQuitButton.to_quit_assessprep_press_the_power_button_and_click_on')}
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};

export const QuitButton = (props: QuitButtonProps) => {
  const {
    withLog,
    userInfo = {},
    className = '',
    quitButtonText = '',
    quitButtonIcon = null,
    action,
  } = props;

  const { t } = useTranslation();
  const appType = getAppType();
  const currentAppVersion = getAppVersion(appType);
  const { executeMethods, enableLockdownApp } = useOfflineAppMethods();
  const { setLogs } = useRealtimeLogs();
  const { setUserInfo } = useRealtimeUserInfo();
  const experience = useExperienceStore(selectExperience);
  const setEndSessionBtnLoading = useRealtimeExperienceStore((s) => s.setEndSessionBtnLoading);
  const experienceSettings = experience?.settings;
  const experienceId = experience?.uid;

  const [loading, setLoading] = useState(false);
  // v3: visible by default so old iOS/mac versions keep the quit affordance.
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    switch (appType) {
      case 'ios':
        if (isNewAppVersion(currentAppVersion, '4.0.0')) {
          setVisible(false);
        }
        break;
      case 'mac':
        if (isNewAppVersion(currentAppVersion, '12.0.0')) {
          setVisible(false);
        }
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appType]);

  const label = quitButtonText || t('CommonText.quit');

  let quitBtn: React.ReactNode | null = null;

  if (appType === 'seb') {
    quitBtn = (
      <Button asChild variant="destructive" className={className} data-testid="v4-quit-seb">
        <a
          href={`${AppConfig.webUrl}/seb_quit.html`}
          onClick={() => {
            withLog &&
              action &&
              setUserInfo(userInfo, {
                log: {
                  logging: true,
                  action: props?.action,
                },
              });
          }}
        >
          {quitButtonIcon} {label}
        </a>
      </Button>
    );
  }

  if (appType === 'mac') {
    quitBtn = (
      <Button
        variant="destructive"
        className={className}
        disabled={loading}
        data-testid="v4-quit-mac"
        onClick={() => {
          setLoading(true);
          withLog && action && setLogs({ logging: true, action: props.action });
          setTimeout(
            () => {
              executeMethods([
                {
                  key: 'quit',
                  value: true,
                  // A quit that works terminates the app, so neither handler
                  // runs. Reaching either one means the app is still here —
                  // the bridge's 5s timeout is the only failure signal quit
                  // can have, which is why `quit` stays OUT of
                  // `noCallbackKeys` in offlineAppService. Before this, the
                  // caller passed no handlers and cleared the spinner
                  // unconditionally, so a dropped quit was silent (APL-12328).
                  onSuccess: () => setLoading(false),
                  onError: () => {
                    setLoading(false);
                    toast.error(t('QuitBtn.the_app_could_not_be_closed_please_close_it_manually'));
                  },
                },
              ]);
            },
            withLog ? 1500 : 0
          );
          // v3: below 12.0.0 the app reports security wrongly after quit.
          if (appType === 'mac' && !isNewAppVersion(currentAppVersion, '12.0.0')) {
            setToLS('securityHasBeenInterrupted', null);
          }
        }}
      >
        {loading && <Spinner className="size-3.5" />}
        {quitButtonIcon} {label}
      </Button>
    );
  }

  if (appType === 'ios') {
    const isEndSession =
      isNewAppVersion(currentAppVersion, '3.2.0') &&
      checkNativeMethodSupported(appType, 'getSecurityStatus');
    quitBtn = (
      <Button
        variant="destructive"
        className={className}
        disabled={loading}
        data-testid="v4-quit-ios"
        onClick={() => {
          setLoading(true);
          setEndSessionBtnLoading(true);
          // v3 quirk kept verbatim: `action` is a string, so this log branch
          // never fires (typeof never 'function') — no iOS quit log write.
          if (withLog && typeof action === 'function') {
            setUserInfo(userInfo, {
              log: {
                logging: true,
                action: props.action,
              },
            });
          }

          setTimeout(
            () => {
              if (isEndSession) {
                executeMethods([
                  {
                    key: 'getSecurityStatus',
                    value: 'status',
                    onSuccess: (value: any) => {
                      if (value === 'false') {
                        toast.success(t('QuitRequested.already_success_message'));
                        if (experienceId) {
                          quitRequestedSuccess(true);
                        }
                        setLoading(false);
                        setEndSessionBtnLoading(false);
                      } else {
                        enableLockdownApp(appType, experienceSettings, {
                          onSuccess: () => {
                            toast.success(
                              t('QuitBtn.lockdown_mode_has_been_disabled_press_the_home_button_or')
                            );
                            setLoading(false);
                            setEndSessionBtnLoading(false);
                          },
                          onError: () => {
                            toast.error(
                              t('QuitBtn.lockdown_mode_could_not_be_disabled_please_try_again')
                            );
                            setLoading(false);
                            setEndSessionBtnLoading(false);
                          },
                          changeSecurityTo: false,
                        });

                        if (experienceId) {
                          quitRequestedSuccess(true);
                        }
                      }
                    },
                  },
                ]);
              } else {
                // iOS versions without getSecurityStatus support.
                executeMethods([
                  {
                    key: 'quit',
                    value: true,
                  },
                ]);
              }
            },
            withLog ? 1500 : 0
          );
        }}
      >
        {loading && <Spinner className="size-3.5" />}
        {quitButtonIcon}
        {isEndSession ? t('CommonText.end_session') : t('CommonText.quit')}
      </Button>
    );
  }

  if (appType === 'cros') {
    quitBtn = <ChromeOSQuitButton />;
  }

  return visible ? quitBtn : null;
};

/* eslint-enable @typescript-eslint/no-explicit-any */
